import React, { useEffect, useState } from 'react';
import { Button, TextField, Select, MenuItem, FormControl, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Account, JournalEntryLine, JournalEntryCreate as JournalEntryCreateType, Segment } from '../../interfaces/accounting.interface';
import { JournalEntryService } from '../../services/journal-entry.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { SegmentService } from '../../services/segment.service';
import { SegmentSettingsService } from '../../services/segment-settings.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { postingAccounts } from '../../utils/accountHierarchy';
import OrderPicker from '../costing/OrderPicker';
import { useOrderTagging } from '../costing/useOrderTagging';
import DepartmentPicker, { useDepartmentOptions } from '../costcentre/DepartmentPicker'; // B4-H
import ProjectSelect, { projectTagLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project

/** The journal description columns are varchar(255); the server answers a longer text with a 400. */
export const JOURNAL_DESCRIPTION_MAX = 255;
export const descriptionCounter = (text: string) => `${text.length}/${JOURNAL_DESCRIPTION_MAX}`;

export const isSegmentRequired = (accountId: string, accounts: Account[]): boolean =>
    !!accountId && accounts.find(a => a.id === accountId)?.segmentFieldPolicy === 'REQUIRED';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

interface LineItem {
    accountId: string;
    entryType: 'DEBIT' | 'CREDIT';
    amount: number;
    description: string;
    segmentId?: string;
    buyerOrderId?: string; // B4-0 (0-5)
    departmentId?: string; // B4-H
    projectId?: string; // P1-A project
    projectLabel?: string; // P1-A: label of a duplicated tag no longer in the picker (closed project)
}

interface DuplicateSource {
    description?: string;
    lines?: Array<{ accountId: string; entryType: 'DEBIT' | 'CREDIT'; amount: number; description?: string; segmentId?: string; buyerOrderId?: string | null; departmentId?: string | null; projectId?: string | null; projectCode?: string | null; projectName?: string | null }>;
}

const JournalEntryCreate: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const duplicateFrom = (location.state as { duplicateFrom?: DuplicateSource } | null)?.duplicateFrom;
    const toast = useToast();
    const orderTagging = useOrderTagging(); // B4-0 (0-5)
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [defaultSegmentId, setDefaultSegmentId] = useState<string>('');
    const [date, setDate] = useState<string>(toLocalIsoDate());
    const [description, setDescription] = useState<string>(duplicateFrom?.description ?? '');
    const [lines, setLines] = useState<LineItem[]>(
        duplicateFrom?.lines && duplicateFrom.lines.length >= 2
            ? duplicateFrom.lines.map(l => ({
                accountId: l.accountId,
                entryType: l.entryType,
                amount: l.amount,
                description: l.description ?? '',
                segmentId: l.segmentId || undefined,
                buyerOrderId: l.buyerOrderId || undefined,
                departmentId: l.departmentId || undefined, // B4-H
                projectId: l.projectId || undefined, // P1-A project: a duplicate keeps the tag
                projectLabel: l.projectCode ? projectTagLabel(l.projectCode, l.projectName) : undefined,
            }))
            : [
                { accountId: '', entryType: 'DEBIT', amount: 0, description: '' },
                { accountId: '', entryType: 'CREDIT', amount: 0, description: '' }
            ]
    );

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                setAccounts(await AccountService.getAllAccounts());
            } catch (err) {
                console.error('Error fetching accounts:', err);
            }
        };
        const fetchSegments = async () => {
            try {
                const res = await SegmentService.getByInstitution();
                const all: Segment[] = res.content ?? [];
                setSegments(all.filter(s => s.active !== false));
            } catch (err) {
                console.error('Error fetching segments:', err);
            }
        };
        fetchAccounts();
        fetchSegments();
        SegmentSettingsService.get()
            .then(s => setDefaultSegmentId(s.defaultSegmentId ?? ''))
            .catch(() => setDefaultSegmentId(''));
    }, []);

    // Header (group) accounts only carry their sub-accounts' totals - postings go to leaf accounts.
    const leafAccounts = React.useMemo(() => postingAccounts(accounts), [accounts]);

    const totalDebits = lines
        .filter(l => l.entryType === 'DEBIT')
        .reduce((sum, l) => sum + Number(l.amount || 0), 0);

    const totalCredits = lines
        .filter(l => l.entryType === 'CREDIT')
        .reduce((sum, l) => sum + Number(l.amount || 0), 0);

    const handleAddLine = () => {
        setLines([...lines, { accountId: '', entryType: 'DEBIT', amount: 0, description: '' }]);
    };

    const handleRemoveLine = (index: number) => {
        if (lines.length <= 2) return;
        setLines(lines.filter((_, i) => i !== index));
    };

    const handleLineChange = (index: number, field: keyof LineItem, value: string | number) => {
        const updated = [...lines];
        (updated[index] as any)[field] = value;
        setLines(updated);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (Math.abs(totalDebits - totalCredits) > 0.001) {
            toast.showError('Total debits must equal total credits');
            return;
        }

        for (const line of lines) {
            if (!line.accountId || line.amount <= 0) {
                toast.showError('All lines must have an account selected and a positive amount');
                return;
            }
        }

        if (lines.some(l => isSegmentRequired(l.accountId, accounts) && !l.segmentId)) {
            toast.showApiError(null, 'Select a segment for every line whose account requires one');
            return;
        }

        try {
            const institutionId = AuthService.getSelectedInstitution();
            const entryLines: JournalEntryLine[] = lines.map(l => ({
                accountId: l.accountId,
                entryType: l.entryType,
                amount: Number(l.amount),
                description: l.description || undefined,
                segmentId: l.segmentId || undefined,
                buyerOrderId: l.buyerOrderId || undefined, // B4-0 (0-5)
                departmentId: l.departmentId || undefined, // B4-H
                projectId: l.projectId || undefined // P1-A project
            }));

            const entryData: JournalEntryCreateType = {
                date,
                description: description || undefined,
                institutionId,
                lines: entryLines
            };

            const created = await JournalEntryService.createEntry(entryData);
            toast.showSuccess('Saved as draft. Post it from the entry page when it is ready.');
            navigate(created?.id ? `/accounting/journal-entries/${created.id}` : '/accounting/journal-entries');
        } catch (err) {
            console.error('Error creating journal entry:', err);
            toast.showApiError(err, 'Failed to create journal entry');
        }
    };

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.journalCreate.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Create Journal Entry</h2>
                <Button data-help="accounting.journalCreate.back" variant="outlined" onClick={() => navigate('/accounting/journal-entries')}>
                    Back to List
                </Button>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                    <TextField data-help="accounting.journalCreate.date"
                        label="Date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                        InputLabelProps={{ shrink: true }}
                        style={{ flex: '1 1 160px', maxWidth: 220 }}
                    />
                    <TextField data-help="accounting.journalCreate.description"
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        inputProps={{ maxLength: JOURNAL_DESCRIPTION_MAX }}
                        helperText={descriptionCounter(description)}
                        style={{ flex: '3 1 260px' }}
                    />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' , flexWrap: 'wrap', gap: '8px' }}>
                    <h3>Entry Lines</h3>
                    <Button data-help="accounting.journalCreate.addLine" variant="outlined" startIcon={<Add />} onClick={handleAddLine}>
                        Add Line
                    </Button>
                </div>

                <ScrollableTable>
                <table data-help-section="accounting.journalCreate.lines" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', minWidth: 980 }}>
                    <thead>
                        <tr>
                            <th data-help="accounting.journalCreate.col.account" style={tableHeaderStyle}>Account</th>
                            <th data-help="accounting.journalCreate.col.type" style={tableHeaderStyle}>Type</th>
                            <th data-help="accounting.journalCreate.col.amount" style={tableHeaderStyle}>Amount</th>
                            <th data-help="accounting.journalCreate.col.lineDescription" style={tableHeaderStyle}>Description</th>
                            <th data-help="accounting.journalCreate.col.segment" style={tableHeaderStyle}>Segment</th>
                            {orderTagging && <th data-help="accounting.journalCreate.col.order" style={tableHeaderStyle}>Order</th>}
                            {departmentOptions.length > 0 && <th data-help="accounting.journalCreate.col.department" style={tableHeaderStyle}>Department</th>}
                            {projectOptions.length > 0 && <th data-help="accounting.journalCreate.col.project" style={tableHeaderStyle}>Project</th>}
                            <th data-help="accounting.journalCreate.col.actions" style={tableHeaderStyle}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line, index) => (
                            <tr key={index}>
                                <td style={{ ...tableCellStyle, minWidth: 260 }}>
                                    <AccountAutocomplete data-help="accounting.journalCreate.line.account"
                                        data-testid={`je-account-${index}`}
                                        accounts={leafAccounts}
                                        value={line.accountId}
                                        label="Account"
                                        placeholder="Select account"
                                        onChange={(accountId) => {
                                            handleLineChange(index, 'accountId', accountId);
                                            if (isSegmentRequired(accountId, accounts) && !line.segmentId && defaultSegmentId) {
                                                handleLineChange(index, 'segmentId', defaultSegmentId);
                                            }
                                        }}
                                    />
                                </td>
                                <td style={tableCellStyle}>
                                    <FormControl fullWidth size="small">
                                        <Select data-help="accounting.journalCreate.line.type"
                                            value={line.entryType}
                                            onChange={(e) => handleLineChange(index, 'entryType', e.target.value)}
                                        >
                                            <MenuItem value="DEBIT">DEBIT</MenuItem>
                                            <MenuItem value="CREDIT">CREDIT</MenuItem>
                                        </Select>
                                    </FormControl>
                                </td>
                                <td style={tableCellStyle}>
                                    <TextField data-help="accounting.journalCreate.line.amount"
                                        type="number"
                                        size="small"
                                        value={line.amount}
                                        onChange={(e) => handleLineChange(index, 'amount', Number(e.target.value))}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        fullWidth
                                    />
                                </td>
                                <td style={tableCellStyle}>
                                    <TextField data-help="accounting.journalCreate.line.description"
                                        size="small"
                                        value={line.description}
                                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                                        inputProps={{ maxLength: JOURNAL_DESCRIPTION_MAX }}
                                        helperText={line.description && line.description.length > 200 ? descriptionCounter(line.description) : undefined}
                                        fullWidth
                                    />
                                </td>
                                <td style={tableCellStyle}>
                                    <SearchableSelect data-help="accounting.journalCreate.line.segment"
                                        data-testid={`je-segment-${index}`}
                                        options={segments}
                                        value={line.segmentId ?? ''}
                                        onChange={(id) => handleLineChange(index, 'segmentId', id)}
                                        getOptionLabel={(s) => `${s.code} — ${s.name}`}
                                        placeholder={isSegmentRequired(line.accountId, accounts) ? 'Select segment…' : '(default)'}
                                        error={isSegmentRequired(line.accountId, accounts) && !line.segmentId}
                                    />
                                </td>
                                {orderTagging && (
                                    <td style={{ ...tableCellStyle, minWidth: 200 }}>
                                        <OrderPicker data-help="accounting.journalCreate.line.order"
                                            value={line.buyerOrderId ?? ''}
                                            label="Order"
                                            onChange={(oid) => handleLineChange(index, 'buyerOrderId', oid)}
                                        />
                                    </td>
                                )}
                                {departmentOptions.length > 0 && (
                                    <td style={{ ...tableCellStyle, minWidth: 200 }}>
                                        <DepartmentPicker data-help="accounting.journalCreate.line.department"
                                            data-testid={`je-department-${index}`}
                                            options={departmentOptions}
                                            value={line.departmentId ?? ''}
                                            onChange={(id) => handleLineChange(index, 'departmentId', id)}
                                        />
                                    </td>
                                )}
                                {projectOptions.length > 0 && (
                                    <td style={{ ...tableCellStyle, minWidth: 200 }}>
                                        <ProjectSelect data-help="accounting.journalCreate.line.project"
                                            data-testid={`je-project-${index}`}
                                            options={projectOptions}
                                            value={line.projectId ?? ''}
                                            currentLabel={line.projectLabel}
                                            onChange={(id) => handleLineChange(index, 'projectId', id)}
                                        />
                                    </td>
                                )}
                                <td style={tableCellStyle}>
                                    <IconButton data-help="accounting.journalCreate.action.remove"
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveLine(index)}
                                        disabled={lines.length <= 2}
                                        aria-label="delete"
                                    >
                                        <Delete />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </ScrollableTable>

                {/* Running Totals */}
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '8px 32px', marginBottom: '24px', fontSize: '16px' }}>
                    <div>
                        <strong>Total Debits:</strong> {formatMoney(totalDebits)}
                    </div>
                    <div>
                        <strong>Total Credits:</strong> {formatMoney(totalCredits)}
                    </div>
                    <div style={{ color: Math.abs(totalDebits - totalCredits) > 0.001 ? 'red' : 'green' }}>
                        <strong>Difference:</strong> {formatMoney(Math.abs(totalDebits - totalCredits))}
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button data-help="accounting.journalCreate.cancel" variant="outlined" onClick={() => navigate('/accounting/journal-entries')}>
                        Cancel
                    </Button>
                    <Button data-help="accounting.journalCreate.save" type="submit" variant="contained" color="primary">
                        Create Entry
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default JournalEntryCreate;
